import { useState, useEffect, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Calculator, Trash2, Wand2, Save, ArrowLeft, BookOpen, ListChecks, Library, Loader2, ChevronLeft, ChevronRight, FileDown, FolderInput, Search, Upload, X, GripVertical } from "lucide-react";

const EUR = (v) => (Number(v) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";
const CAT_LABEL = { prestation: "Prestation PPD", tce: "Travaux TCE", agencement: "Agencement sur-mesure", mobilier: "Mobilier & déco", centre: "Contraintes centre", equipement: "Équipements" };
const CONF = {
  reel_recent: { dot: "bg-emerald-500", label: "Prix réel récent" },
  historique: { dot: "bg-amber-500", label: "Historique" },
  provision: { dot: "bg-rose-500", label: "Provision" },
};

export default function Estimatif() {
  const [tab, setTab] = useState("estimations");
  const [current, setCurrent] = useState(null); // estimation id being edited

  return (
    <div className="space-y-6" data-testid="estimatif-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Calculator className="text-[#C2502F]" size={28}/> Estimation travaux</h1>
          <p className="text-sm text-stone-500 mt-1">Estimatif intelligent retail / restauration / centre commercial — questionnaire + bibliothèque de prix évolutive.</p>
        </div>
      </div>

      {!current && (
        <div className="flex gap-1 border-b border-stone-200">
          {[["estimations", "Estimations", ListChecks], ["bibliotheque", "Bibliothèque d'ouvrages", Library], ["questionnaire", "Questionnaire", BookOpen]].map(([k, lbl, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`estim-tab-${k}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === k ? "border-[#C2502F] text-[#C2502F]" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
              <Icon size={15}/> {lbl}
            </button>
          ))}
        </div>
      )}

      {current ? <Runner eid={current} onBack={() => setCurrent(null)} />
        : tab === "estimations" ? <EstimationsList onOpen={setCurrent} />
        : tab === "bibliotheque" ? <Bibliotheque />
        : <QuestionnaireAdmin />}
    </div>
  );
}

/* ---------------- Estimations list ---------------- */
function EstimationsList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [nom, setNom] = useState("");
  const load = async () => { const { data } = await api.get("/estimatif/estimations"); setRows(data); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    const { data } = await api.post("/estimatif/estimations", { nom: nom || "Nouvelle estimation" });
    setNom(""); onOpen(data.id);
  };
  const del = async (id) => { if (!window.confirm("Supprimer cette estimation ?")) return; await api.delete(`/estimatif/estimations/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input data-testid="estim-new-name" value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom de l'estimation (ex: GEOX Parly)" className="border border-stone-300 rounded px-3 py-2 text-sm flex-1 max-w-md"/>
        <button data-testid="estim-create-btn" onClick={create} className="bg-[#C2502F] text-white px-4 py-2 rounded font-semibold text-sm flex items-center gap-1.5"><Plus size={16}/> Nouvelle estimation</button>
      </div>
      <div className="bg-white border border-stone-200 rounded-lg divide-y">
        {rows.length === 0 && <div className="p-6 text-sm text-stone-400 text-center">Aucune estimation pour l'instant.</div>}
        {rows.map(r => (
          <div key={r.id} className="flex items-center justify-between p-3 hover:bg-stone-50" data-testid={`estim-row-${r.id}`}>
            <button onClick={() => onOpen(r.id)} className="text-left flex-1">
              <div className="font-semibold text-sm">{r.nom}</div>
              <div className="text-xs text-stone-500">{r.statut} · {EUR(r.totaux?.total)} {r.totaux?.par_m2 ? `· ${EUR(r.totaux.par_m2)}/m²` : ""}</div>
            </button>
            {typeof r.fiabilite?.score === "number" && <span className="text-xs font-semibold mr-3 text-stone-600">Fiabilité {r.fiabilite.score}%</span>}
            <button onClick={() => del(r.id)} data-testid={`estim-del-${r.id}`} className="text-stone-400 hover:text-rose-600 p-1"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Runner (questionnaire + panel + DPGF) ---------------- */
function Runner({ eid, onBack }) {
  const [schema, setSchema] = useState(null);
  const [est, setEst] = useState(null);
  const [answers, setAnswers] = useState({});
  const [prestation, setPrestation] = useState({ enabled: false, type: "ppd", montant: 0, description: "" });
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [enseigne, setEnseigne] = useState("");
  const [enseignes, setEnseignes] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [q, e, pr, en] = await Promise.all([
        api.get("/estimatif/questionnaire"),
        api.get(`/estimatif/estimations/${eid}`),
        api.get("/projects"),
        api.get("/enseignes"),
      ]);
      setSchema(q.data); setEst(e.data);
      setAnswers({ ...(e.data.variables || {}), ...(e.data.answers || {}) });
      setPrestation(e.data.prestation && Object.keys(e.data.prestation).length ? e.data.prestation : { enabled: false, type: "ppd", montant: 0, description: "" });
      setProjectId(e.data.project_id || "");
      setEnseigne(e.data.enseigne || "");
      setProjects((pr.data || []).map(p => ({ id: p.id, nom: p.nom, enseigne: p.enseigne })));
      setEnseignes((en.data || []).map(x => x.nom));
    })();
  }, [eid]);

  const setA = (key, val) => setAnswers(p => ({ ...p, [key]: val }));

  const linkProject = async (pid) => {
    setProjectId(pid);
    if (!pid) return;
    try {
      const { data } = await api.get(`/estimatif/project-defaults/${pid}`);
      setAnswers(p => ({ ...p, franchiseur: data.franchiseur || p.franchiseur }));
      if (data.enseigne) setEnseigne(data.enseigne);
      setPrestation(p => ({
        ...p, enabled: p.enabled || (data.montant_vendu > 0),
        montant: p.montant || data.montant_vendu || 0,
        description: p.description || (data.prestation_type ? `Prestation Pourquoi Pas Design (${data.prestation_type})` : "Prestation Pourquoi Pas Design"),
      }));
      toast.success(`Projet lié : ${data.nom}${data.enseigne ? " · " + data.enseigne : ""}`);
    } catch { /* noop */ }
  };

  const payload = () => ({ variables: answers, answers, prestation, project_id: projectId || null, enseigne: enseigne || "" });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/estimatif/estimations/${eid}`, payload()); setEst(data); toast.success("Enregistré"); }
    finally { setBusy(false); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      await api.put(`/estimatif/estimations/${eid}`, payload());
      const { data } = await api.post(`/estimatif/estimations/${eid}/generate`);
      setEst(data); toast.success(`${data.lignes.length} lignes générées`);
    } finally { setBusy(false); }
  };
  const exportPdf = async () => {
    const { data } = await api.get(`/estimatif/estimations/${eid}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data); window.open(url, "_blank");
  };
  const attachToProject = async () => {
    if (!projectId) { toast.error("Liez d'abord un projet"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/estimatif/estimations/${eid}/attach`); toast.success("Estimatif enregistré dans le projet : " + data.filename); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const visible = (cond) => {
    if (!cond) return true;
    const v = answers[cond.question_key];
    const arr = Array.isArray(v) ? v : [v];
    return (cond.in || []).some(x => arr.includes(x));
  };
  const sectionVisible = (s) => visible(s.condition);

  if (!schema || !est) return <div className="p-8 text-center text-stone-400"><Loader2 className="animate-spin inline" /> Chargement…</div>;

  const baseSections = (schema.sections || []).filter(s => s.phase !== "detail" && sectionVisible(s));
  const STEPS = ["Cadrage du projet", "Affinage par lot", "Estimatif global"];

  const goStep2 = async () => { await generate(); setStep(2); };

  const Panel = () => (
    <div className="bg-stone-900 text-white rounded-lg p-5 sticky top-4" data-testid="estim-panel">
      <div className="text-xs uppercase tracking-wide text-stone-400 mb-3">Estimation projet</div>
      {["prestation", "tce", "agencement", "mobilier", "centre", "equipement"].filter(c => (est.totaux?.[c] || 0) > 0).map(c => (
        <div key={c} className="flex justify-between text-sm py-1">
          <span className="text-stone-300">{CAT_LABEL[c]}</span><span className="font-semibold">{EUR(est.totaux?.[c])}</span>
        </div>
      ))}
      <div className="border-t border-stone-700 mt-2 pt-2 flex justify-between">
        <span className="font-bold">TOTAL HT</span><span className="font-bold text-lg" data-testid="estim-total">{EUR(est.totaux?.total)}</span>
      </div>
      {est.totaux?.par_m2 > 0 && <div className="text-right text-xs text-stone-400">{EUR(est.totaux.par_m2)} / m²</div>}
      {est.totaux?.budget_cible > 0 && (
        <div className="mt-2 text-sm flex justify-between"><span className="text-stone-300">Écart budget</span>
          <span className={est.totaux.ecart_budget > 0 ? "text-rose-400 font-semibold" : "text-emerald-400 font-semibold"}>{est.totaux.ecart_budget > 0 ? "+" : ""}{EUR(est.totaux.ecart_budget)}</span>
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-stone-700">
        <div className="flex justify-between text-sm mb-2"><span className="text-stone-300">Fiabilité</span><span className="font-bold" data-testid="estim-fiabilite">{est.fiabilite?.score || 0} %</span></div>
        <div className="h-2 rounded-full overflow-hidden flex bg-stone-700">
          <div className="bg-emerald-500" style={{ width: `${est.fiabilite?.pct_reel || 0}%` }} />
          <div className="bg-amber-500" style={{ width: `${est.fiabilite?.pct_historique || 0}%` }} />
          <div className="bg-rose-500" style={{ width: `${est.fiabilite?.pct_provision || 0}%` }} />
        </div>
        <div className="text-[11px] text-stone-400 mt-2 space-y-0.5">
          <div>🟢 {est.fiabilite?.pct_reel || 0}% prix réels récents</div>
          <div>🟠 {est.fiabilite?.pct_historique || 0}% historiques</div>
          <div>🔴 {est.fiabilite?.pct_provision || 0}% provisions</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-stone-500 flex items-center gap-1 hover:text-stone-800"><ArrowLeft size={15}/> Retour aux estimations</button>

      {/* Stepper avec flèches */}
      <div className="flex items-center justify-between bg-white border border-stone-200 rounded-lg p-2">
        <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} data-testid="estim-prev-step" className="p-2 text-stone-500 disabled:opacity-30 hover:text-[#C2502F]"><ChevronLeft size={20}/></button>
        <div className="flex-1 flex items-center justify-center gap-2">
          {STEPS.map((label, i) => (
            <button key={i} onClick={() => setStep(i + 1)} data-testid={`estim-step-${i + 1}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition ${step === i + 1 ? "bg-[#C2502F] text-white" : "text-stone-500 hover:bg-stone-100"}`}>
              <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${step === i + 1 ? "bg-white text-[#C2502F]" : "bg-stone-200"}`}>{i + 1}</span>
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <button onClick={() => setStep(s => Math.min(3, s + 1))} disabled={step === 3} data-testid="estim-next-step" className="p-2 text-stone-500 disabled:opacity-30 hover:text-[#C2502F]"><ChevronRight size={20}/></button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{est.nom} <span className="text-stone-400 font-normal text-sm">· Étape {step}/3</span></h2>
            <button onClick={save} disabled={busy} data-testid="estim-save-btn" className="border border-stone-300 px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1.5"><Save size={14}/> Enregistrer</button>
          </div>

          {/* ÉTAPE 1 — Cadrage */}
          {step === 1 && (<>
            <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
              <div className="font-display font-bold text-[#C2502F]">Projet lié</div>
              <select value={projectId} onChange={e => linkProject(e.target.value)} data-testid="estim-project-select" className="border border-stone-300 rounded px-3 py-2 text-sm w-full">
                <option value="">— Aucun projet (estimation libre) —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.nom}{p.enseigne ? ` — ${p.enseigne}` : ""}</option>)}
              </select>
              <div className="mt-2">
                <label className="block text-xs font-medium text-stone-500 mb-1">Enseigne (référentiels)</label>
                <select value={enseigne} onChange={e => setEnseigne(e.target.value)} data-testid="estim-enseigne-select" className="border border-stone-300 rounded px-3 py-2 text-sm w-full">
                  <option value="">— Aucune enseigne —</option>
                  {enseignes.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="border-t border-stone-100 pt-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={!!prestation.enabled} onChange={e => setPrestation(p => ({ ...p, enabled: e.target.checked }))} data-testid="estim-prestation-toggle"/>
                  Ajouter ma prestation (lot préalable)
                </label>
                {prestation.enabled && (
                  <div className="flex flex-wrap gap-2 mt-2 items-center">
                    <select value={prestation.type} onChange={e => setPrestation(p => ({ ...p, type: e.target.value, description: e.target.value === "archi" ? "Prestation architecte" : "Prestation Pourquoi Pas Design" }))} className="border border-stone-300 rounded px-2 py-1.5 text-sm">
                      <option value="ppd">Prestation Pourquoi Pas Design</option>
                      <option value="archi">Prestation architecte</option>
                    </select>
                    <input type="number" value={prestation.montant || ""} onChange={e => setPrestation(p => ({ ...p, montant: Number(e.target.value) }))} placeholder="Montant HT" className="border border-stone-300 rounded px-2 py-1.5 text-sm w-32" data-testid="estim-prestation-montant"/>
                    <span className="text-xs text-stone-400">tiré de la fiche projet (modifiable)</span>
                  </div>
                )}
              </div>
            </div>
            {baseSections.map(s => (
              <div key={s.id} className="bg-white border border-stone-200 rounded-lg p-4" data-testid={`estim-section-${s.code}`}>
                <div className="font-display font-bold text-[#C2502F] mb-3">{s.code} — {s.titre}</div>
                <div className="space-y-4">
                  {(s.questions || []).filter(q => visible(q.condition)).map(q => (
                    <QuestionField key={q.id} q={q} value={answers[q.key]} onChange={(v) => setA(q.key, v)} />
                  ))}
                </div>
              </div>
            ))}
            <button onClick={goStep2} disabled={busy} data-testid="estim-to-step2" className="bg-[#C2502F] text-white px-5 py-2.5 rounded font-semibold flex items-center gap-2">{busy ? <Loader2 size={16} className="animate-spin"/> : <Wand2 size={16}/>} Générer &amp; affiner <ChevronRight size={16}/></button>
          </>)}

          {/* ÉTAPE 2 — Affinage par lot */}
          {step === 2 && (
            <Step2Refine eid={eid} est={est} setEst={setEst} schema={schema} answers={answers} setA={setA} visible={visible} enseigne={enseigne} onBack={() => setStep(1)} onNext={() => setStep(3)} />
          )}

          {/* ÉTAPE 3 — Estimatif global */}
          {step === 3 && (<>
            <div className="flex flex-wrap gap-2">
              <button onClick={exportPdf} data-testid="estim-export-pdf" className="border border-stone-300 px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"><FileDown size={15}/> Exporter (PDF)</button>
              {projectId && <button onClick={attachToProject} disabled={busy} data-testid="estim-attach" className="border border-stone-300 px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"><FolderInput size={15}/> Enregistrer dans le projet</button>}
              <button onClick={() => setStep(2)} className="text-sm text-stone-500 flex items-center gap-1 px-2"><ChevronLeft size={15}/> Affiner encore</button>
            </div>
            {(est.lignes || []).length > 0
              ? <DpgfTable eid={eid} est={est} setEst={setEst} />
              : <div className="bg-white border border-stone-200 rounded-lg p-6 text-center text-sm text-stone-400">Aucune ligne. Revenez à l'étape 1 pour générer.</div>}
          </>)}
        </div>

        <div className="space-y-4"><Panel /></div>
      </div>
    </div>
  );
}

const MARKET_CATS = ["mobilier", "equipement", "agencement"];

function PriceSuggestModal({ open, line, onApply, onClose }) {
  const [desc, setDesc] = useState("");
  const [ctx, setCtx] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDesc(line?.description || ""); setCtx(""); setRes(null); } }, [open, line]);
  if (!open) return null;
  const estimate = async () => {
    if (!desc.trim()) { toast.error("Ajoutez une description courte"); return; }
    setBusy(true);
    try { const { data } = await api.post("/estimatif/price-suggest", { description: desc, unite: line?.unite || "u", contexte: ctx }); setRes(data); }
    catch { toast.error("Estimation IA indisponible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="price-modal">
      <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold flex items-center gap-2"><Search size={16} className="text-[#C2502F]"/> Prix marché (IA)</h3>
        <p className="text-xs text-stone-500">Décrivez brièvement l'élément (style, matière, dimensions, gamme) pour obtenir un prix cohérent sur le marché français.</p>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} placeholder="Ex : chaise bistrot métal noir assise bois, gamme standard" className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="price-modal-desc"/>
        <input value={ctx} onChange={e => setCtx(e.target.value)} placeholder="Contexte (facultatif) : restaurant, boutique…" className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="price-modal-ctx"/>
        <button onClick={estimate} disabled={busy} className="bg-[#C2502F] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5" data-testid="price-modal-estimate">{busy ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Estimer le prix</button>
        {res && (
          <div className="border border-stone-200 rounded p-3 bg-stone-50 space-y-2" data-testid="price-modal-result">
            <div className="text-xs text-stone-500">{res.justification}</div>
            {res.niveaux ? (
              <div className="grid grid-cols-3 gap-2">
                {[["eco", "Éco"], ["standard", "Standard"], ["premium", "Premium"]].map(([k, label]) => (
                  <button key={k} onClick={() => { onApply(res.niveaux[k]); onClose(); }} disabled={!res.niveaux[k]} className="border border-[#C2502F] rounded p-2 text-center hover:bg-[#C2502F] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-current" data-testid={`price-modal-${k}`}>
                    <div className="text-[10px] uppercase tracking-wide text-stone-400">{label}</div>
                    <div className="text-base font-bold">{EUR(res.niveaux[k])}</div>
                  </button>
                ))}
              </div>
            ) : (
              <button onClick={() => { onApply(res.pu_ht); onClose(); }} className="border border-[#C2502F] text-[#C2502F] px-3 py-1.5 rounded text-sm font-semibold" data-testid="price-modal-apply">Appliquer {EUR(res.pu_ht)}</button>
            )}
            <div className="text-[10px] text-stone-400">/ {line?.unite || "u"} HT — cliquez une gamme pour l'appliquer</div>
          </div>
        )}
        <button onClick={onClose} className="text-xs text-stone-400 block" data-testid="price-modal-close">Fermer</button>
      </div>
    </div>
  );
}

function Step2Refine({ eid, est, setEst, schema, answers, setA, visible, enseigne, onBack, onNext }) {
  const [lignes, setLignes] = useState(est.lignes || []);
  const [allLots, setAllLots] = useState([]);
  const [extraLots, setExtraLots] = useState([]);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [priceIdx, setPriceIdx] = useState(null);
  const [referentiels, setReferentiels] = useState([]);

  useEffect(() => { setLignes(est.lignes || []); }, [est.lignes]);
  useEffect(() => { api.get("/estimatif/lots").then(({ data }) => setAllLots((data.lots || []).map(l => l.lot))); }, []);
  useEffect(() => {
    if (!enseigne) { setReferentiels([]); return; }
    api.get("/estimatif/referentiels", { params: { enseigne, actif: true } }).then(({ data }) => setReferentiels(data || [])).catch(() => {});
  }, [enseigne]);

  const makeRefLine = (rf, a) => ({
    id: "r" + rf.id.slice(0, 4) + a.id, lot: rf.lot || "DIVERS", description: a.designation,
    unite: a.unite || "u", quantite: a.qte_defaut || 1, pu: a.pu_ht || 0,
    montant: Number(a.qte_defaut || 1) * Number(a.pu_ht || 0), categorie: _catForLot(rf.lot || ""),
    auto: false, ref_id: rf.id, ref_article_id: a.id, source_prix: "Référentiel " + rf.nom,
    niveau_confiance: "reel_recent", nb_references: 1,
  });
  useEffect(() => {
    const packs = referentiels.filter(r => r.mode === "pack");
    if (!packs.length) return;
    setLignes(prev => {
      const present = new Set(prev.map(l => l.ref_article_id).filter(Boolean));
      const add = [];
      packs.forEach(rf => (rf.articles || []).forEach(a => { if (!present.has(a.id)) add.push(makeRefLine(rf, a)); }));
      return add.length ? [...prev, ...add] : prev;
    });
  }, [referentiels]); // eslint-disable-line
  const catRefs = referentiels.filter(r => r.mode === "catalogue");
  const hasArticle = (aid) => lignes.some(l => l.ref_article_id === aid);
  const toggleCatalogueArticle = (rf, a, checked) => setLignes(prev => checked ? [...prev, makeRefLine(rf, a)] : prev.filter(l => l.ref_article_id !== a.id));

  const [ccList, setCcList] = useState([]);
  const isCC = (answers.situation === "Centre commercial");
  useEffect(() => {
    if (!isCC) { setCcList([]); return; }
    api.get("/estimatif/ouvrages", { params: { lot: "CONTRAINTES CENTRE COMMERCIAL" } })
      .then(({ data }) => setCcList((data || []).filter(o => (o.sous_lot || "") === "CENTRE COMMERCIAL")))
      .catch(() => {});
  }, [isCC]);
  const makeCCLine = (o) => ({
    id: "cc" + o.id, lot: "CONTRAINTES CENTRE COMMERCIAL", description: o.description, unite: o.unite || "ens",
    quantite: 1, pu: o.pu_conseille || 0, montant: o.pu_conseille || 0, categorie: "centre", auto: false,
    cc_ouvrage_id: o.id, source_prix: "Centre commercial", niveau_confiance: o.niveau_confiance || "provision", nb_references: o.nb_references || 0,
  });
  const hasCC = (oid) => lignes.some(l => l.cc_ouvrage_id === oid);
  const toggleCC = (o, checked) => setLignes(prev => checked ? [...prev, makeCCLine(o)] : prev.filter(l => l.cc_ouvrage_id !== o.id));

  const detailByLot = useMemo(() => {
    const m = {}; (schema.sections || []).filter(s => s.phase === "detail").forEach(s => { m[s.lot] = s; }); return m;
  }, [schema]);
  const presentLots = useMemo(() => {
    const s = [];
    (lignes || []).forEach(l => { if (!s.includes(l.lot)) s.push(l.lot); });
    extraLots.forEach(l => { if (!s.includes(l)) s.push(l); });
    return s;
  }, [lignes, extraLots]);
  const linesByLot = useMemo(() => {
    const g = {}; (lignes || []).forEach(l => { (g[l.lot] = g[l.lot] || []).push(l); }); return g;
  }, [lignes]);

  const updLine = (id, k, v) => setLignes(p => p.map(l => l.id === id ? { ...l, [k]: v, montant: k === "quantite" ? Number(v) * l.pu : k === "pu" ? l.quantite * Number(v) : l.montant } : l));
  const delLine = (id) => setLignes(p => p.filter(l => l.id !== id));
  const addFree = (lot) => { const id = "m" + Math.random().toString(36).slice(2, 9); setLignes(p => [...p, { id, lot, description: "", unite: "u", quantite: 1, pu: 0, montant: 0, categorie: _catForLot(lot), auto: false, niveau_confiance: "provision", nb_references: 0 }]); setOpen(o => ({ ...o, [lot]: true })); };
  const addLotCard = (lot) => { if (!lot) return; setExtraLots(p => p.includes(lot) ? p : [...p, lot]); addFree(lot); };
  const applyPrice = (id, pu) => updLine(id, "pu", pu);
  const [autoBusy, setAutoBusy] = useState(null);
  const autoPrice = async (l) => {
    if (!MARKET_CATS.includes(_catForLot(l.lot))) return;
    if (Number(l.pu) > 0) return; // ne jamais écraser un prix saisi
    const d = (l.description || "").trim();
    if (d.length < 4) return;
    setAutoBusy(l.id);
    try {
      const { data } = await api.post("/estimatif/price-auto", { description: d, unite: l.unite, lot: l.lot });
      if (data.pu_ht > 0) { updLine(l.id, "pu", data.pu_ht); toast.success(`Prix ${data.source === "bibliotheque" ? "bibliothèque" : "marché (IA)"} : ${data.pu_ht} €`); }
    } catch { /* silencieux */ }
    finally { setAutoBusy(null); }
  };

  const persist = async (regen) => {
    setBusy(true);
    try {
      const { data: saved } = await api.put(`/estimatif/estimations/${eid}`, { variables: answers, answers, lignes });
      if (regen) {
        const { data } = await api.post(`/estimatif/estimations/${eid}/generate`);
        setEst(data); toast.success("Affinage recalculé");
      } else { setEst(saved); toast.success("Affinage enregistré"); }
    } finally { setBusy(false); }
  };

  const priceLine = priceIdx != null ? (lignes.find(l => l.id === priceIdx) || null) : null;
  const availableLots = allLots.filter(l => !presentLots.includes(l));

  return (
    <>
      <p className="text-sm text-stone-500">Affinez chaque lot présent : précisez les quantités clés et ajoutez des éléments libres (mobilier, déco, cuisine…). Le bouton <b>Prix marché (IA)</b> est disponible pour le mobilier, la déco, l'agencement et la cuisine.</p>
      {ccList.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-2" data-testid="estim-cc-checklist">
          <div className="font-display font-bold text-[#C2502F]">Centre commercial — postes spécifiques</div>
          <p className="text-xs text-stone-500">Cochez les postes imposés par le centre (non systématiques). Prix modifiables ensuite dans le lot.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {ccList.map(o => (
              <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid="estim-cc-item">
                <input type="checkbox" checked={hasCC(o.id)} onChange={e => toggleCC(o, e.target.checked)}/>
                <span className="flex-1 truncate">{o.description}</span>
                <span className="text-xs text-stone-400 shrink-0">{EUR(o.pu_conseille)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {catRefs.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3" data-testid="estim-catalogues">
          <div className="font-display font-bold text-[#C2502F]">Catalogues enseigne — {enseigne}</div>
          {catRefs.map(rf => (
            <div key={rf.id}>
              <div className="text-sm font-semibold text-stone-600">{rf.nom} <span className="text-xs text-stone-400">({rf.lot})</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                {(rf.articles || []).map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid="estim-catalogue-article">
                    <input type="checkbox" checked={hasArticle(a.id)} onChange={e => toggleCatalogueArticle(rf, a, e.target.checked)}/>
                    <span className="flex-1 truncate">{a.designation}</span>
                    <span className="text-xs text-stone-400 shrink-0">{EUR(a.pu_ht)}/{a.unite}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {presentLots.length === 0 && <div className="bg-white border border-stone-200 rounded-lg p-6 text-center text-sm text-stone-400">Aucun lot pour l'instant. Revenez à l'étape 1 et générez, ou ajoutez un lot à affiner ci-dessous.</div>}

      {presentLots.map(lot => {
        const det = detailByLot[lot];
        const lls = linesByLot[lot] || [];
        const autoLls = lls.filter(l => l.auto);
        const freeLls = lls.filter(l => !l.auto);
        const market = MARKET_CATS.includes(_catForLot(lot));
        const isOpen = open[lot] ?? true;
        return (
          <div key={lot} className="bg-white border border-stone-200 rounded-lg" data-testid={`estim-lot-card-${_catForLot(lot)}`}>
            <button onClick={() => setOpen(o => ({ ...o, [lot]: !isOpen }))} className="w-full flex items-center justify-between p-4" data-testid="estim-lot-toggle">
              <span className="font-display font-bold text-[#C2502F] flex items-center gap-2"><span className="text-stone-400">{isOpen ? "▾" : "▸"}</span>{lot}</span>
              <span className="text-xs text-stone-400">{lls.length} ligne(s){market ? " · Prix marché IA" : ""}</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-4 border-t border-stone-100 pt-3">
                {det && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(det.questions || []).filter(q => visible(q.condition)).map(q => (
                      <QuestionField key={q.id} q={q} value={answers[q.key]} onChange={(v) => setA(q.key, v)} />
                    ))}
                  </div>
                )}
                {autoLls.length > 0 && (
                  <div className="text-xs">
                    <div className="text-stone-400 uppercase tracking-wide mb-1">Généré automatiquement</div>
                    {autoLls.map(l => (
                      <div key={l.id} className="flex justify-between gap-2 py-0.5 text-stone-600 border-b border-stone-50">
                        <span className="truncate">{l.description}</span>
                        <span className="text-stone-500 whitespace-nowrap">{l.quantite} {l.unite} × {EUR(l.pu)} = <b>{EUR(l.montant)}</b></span>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide mb-1">Éléments personnalisés (champs libres)</div>
                  {freeLls.map(l => (
                    <div key={l.id} className="flex flex-wrap items-center gap-1.5 py-1" data-testid="estim-free-line">
                      <input value={l.description} onChange={e => updLine(l.id, "description", e.target.value)} onBlur={() => autoPrice(l)} placeholder="Description (ex: chaise bistrot métal noir)" className="border border-stone-200 rounded px-2 py-1 text-sm flex-1 min-w-[180px]" data-testid="estim-free-desc"/>
                      <input value={l.unite} onChange={e => updLine(l.id, "unite", e.target.value)} className="w-12 border border-stone-200 rounded px-1 py-1 text-sm" title="Unité"/>
                      <input type="number" value={l.quantite} onChange={e => updLine(l.id, "quantite", e.target.value)} className="w-14 border border-stone-200 rounded px-1 py-1 text-sm text-right" title="Quantité"/>
                      <input type="number" value={l.pu} onChange={e => updLine(l.id, "pu", e.target.value)} className="w-20 border border-stone-200 rounded px-1 py-1 text-sm text-right" title="PU HT" data-testid="estim-free-pu"/>
                      <span className="text-sm font-semibold w-20 text-right">{EUR(l.montant)}</span>
                      {autoBusy === l.id && <Loader2 size={13} className="animate-spin text-[#C2502F]" title="Recherche du prix…"/>}
                      {market && <button onClick={() => setPriceIdx(l.id)} data-testid="estim-market-price" title="Prix marché (IA)" className="text-[#C2502F] hover:opacity-70 p-1"><Search size={14}/></button>}
                      <button onClick={() => delLine(l.id)} className="text-stone-300 hover:text-rose-600 p-1"><Trash2 size={14}/></button>
                    </div>
                  ))}
                  <button onClick={() => addFree(lot)} data-testid="estim-add-free" className="mt-1 text-sm text-[#C2502F] font-semibold flex items-center gap-1"><Plus size={14}/> Ajouter un élément</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white border border-stone-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-stone-500">Ajouter un lot à affiner :</span>
        <select value="" onChange={e => addLotCard(e.target.value)} className="border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="estim-add-lot-card">
          <option value="">— choisir un lot —</option>
          {availableLots.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={onBack} className="border border-stone-300 px-4 py-2.5 rounded font-semibold flex items-center gap-1.5"><ChevronLeft size={16}/> Retour cadrage</button>
        <button onClick={() => persist(false)} disabled={busy} data-testid="estim-save-refine" className="border border-stone-300 px-4 py-2.5 rounded font-semibold flex items-center gap-1.5"><Save size={16}/> Enregistrer l'affinage</button>
        <button onClick={async () => { await persist(true); onNext(); }} disabled={busy} data-testid="estim-to-step3" className="bg-[#C2502F] text-white px-5 py-2.5 rounded font-semibold flex items-center gap-2">{busy ? <Loader2 size={16} className="animate-spin"/> : <Wand2 size={16}/>} Recalculer &amp; voir l'estimatif <ChevronRight size={16}/></button>
      </div>

      <PriceSuggestModal open={priceIdx != null} line={priceLine} onApply={(pu) => priceLine && applyPrice(priceLine.id, pu)} onClose={() => setPriceIdx(null)} />
    </>
  );
}

function QuestionField({ q, value, onChange }) {
  const base = "border border-stone-300 rounded px-3 py-2 text-sm";
  if (q.type === "number") {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{q.label} {q.unit && <span className="text-stone-400">({q.unit})</span>}</label>
        <input type="number" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={`${base} w-40`} data-testid={`q-${q.key}`}/>
      </div>
    );
  }
  if (q.type === "boolean") {
    return (
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium">{q.label}</label>
        <div className="flex gap-1">
          {[["Oui", true], ["Non", false]].map(([lbl, v]) => (
            <button key={lbl} onClick={() => onChange(v)} data-testid={`q-${q.key}-${lbl}`}
              className={`px-3 py-1 rounded text-sm border ${value === v ? "bg-[#C2502F] text-white border-[#C2502F]" : "border-stone-300 text-stone-600"}`}>{lbl}</button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{q.label}</label>
        <div className="flex flex-wrap gap-1.5">
          {(q.options || []).map(o => (
            <button key={o.id} onClick={() => toggle(o.value || o.label)} data-testid={`q-${q.key}-opt`}
              className={`px-2.5 py-1 rounded-full text-xs border ${arr.includes(o.value || o.label) ? "bg-[#C2502F] text-white border-[#C2502F]" : "border-stone-300 text-stone-600 hover:border-stone-400"}`}>{o.label}</button>
          ))}
        </div>
      </div>
    );
  }
  // single / etat
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{q.label}</label>
      <div className="flex flex-wrap gap-1.5">
        {(q.options || []).map(o => (
          <button key={o.id} onClick={() => onChange(o.value || o.label)} data-testid={`q-${q.key}-opt`}
            className={`px-2.5 py-1 rounded text-xs border ${(value === (o.value || o.label)) ? "bg-[#C2502F] text-white border-[#C2502F]" : "border-stone-300 text-stone-600 hover:border-stone-400"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function _catForLot(lot) {
  const u = (lot || "").toUpperCase();
  if (u.includes("MOBILIER")) return "mobilier";
  if (u.includes("CUISINE")) return "equipement";
  if (u.includes("AGENCEMENT")) return "agencement";
  if (u.includes("CENTRE")) return "centre";
  if (u.includes("PRESTATION")) return "prestation";
  return "tce";
}

function DpgfTable({ eid, est, setEst }) {
  const [lignes, setLignes] = useState(est.lignes);
  const [priceIdx, setPriceIdx] = useState(null);
  useEffect(() => { setLignes(est.lignes); }, [est.lignes]);
  const upd = (i, k, v) => setLignes(p => p.map((l, idx) => idx === i ? { ...l, [k]: v, montant: k === "quantite" ? Number(v) * l.pu : k === "pu" ? l.quantite * Number(v) : l.montant } : l));
  const del = (i) => setLignes(p => p.filter((_, idx) => idx !== i));
  const addManual = (lot) => setLignes(p => [...p, {
    id: "m" + Math.random().toString(36).slice(2, 9), lot, description: "Nouvelle ligne", unite: "u",
    quantite: 1, pu: 0, montant: 0, categorie: _catForLot(lot), auto: false, niveau_confiance: "provision", nb_references: 0,
  }]);
  const applyPrice = (id, pu) => { const i = lignes.findIndex(l => l.id === id); if (i >= 0) upd(i, "pu", pu); };
  const save = async () => {
    const { data } = await api.put(`/estimatif/estimations/${eid}`, { lignes });
    setEst(data); toast.success("Estimatif enregistré");
  };
  const grouped = useMemo(() => {
    const g = {}; lignes.forEach((l, i) => { (g[l.lot] = g[l.lot] || []).push({ ...l, _i: i }); }); return g;
  }, [lignes]);
  const allLots = useMemo(() => Array.from(new Set([...(est.lignes || []).map(l => l.lot), "MOBILIER PRÊT-À-POSER & DÉCO", "CUISINE - LABORATOIRE"])), [est.lignes]);

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="estim-dpgf">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">Estimatif détaillé (DPGF)</h3>
        <button onClick={save} data-testid="estim-dpgf-save" className="bg-[#C2502F] text-white px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1.5"><Save size={14}/> Enregistrer l'estimatif</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-stone-500 border-b">
            <th className="py-2">Description</th><th>Unité</th><th className="text-right">Qté</th><th className="text-right">PU HT</th><th className="text-right">Montant</th><th className="text-center">Prix</th><th></th>
          </tr></thead>
          <tbody>
            {Object.entries(grouped).map(([lot, items]) => {
              const showMarket = MARKET_CATS.includes(_catForLot(lot));
              return (
                <Fragment key={lot}>
                  <tr className="bg-stone-50"><td colSpan={7} className="py-1.5 px-1 font-semibold text-xs uppercase text-stone-600">{lot}
                    <button onClick={() => addManual(lot)} data-testid="estim-add-line" className="ml-2 text-[#C2502F] font-semibold normal-case">+ ligne</button></td></tr>
                  {items.map(l => (
                    <tr key={l.id} className="border-b border-stone-100">
                      <td className="py-1.5 pr-2">
                        {l.auto ? l.description : <input value={l.description} onChange={e => upd(l._i, "description", e.target.value)} className="w-full border border-stone-200 rounded px-1 py-0.5" data-testid="estim-line-desc"/>}
                        {!l.auto && <span className="ml-1 text-[10px] text-blue-600">(manuel)</span>}
                      </td>
                      <td className="text-stone-500">{l.auto ? l.unite : <input value={l.unite} onChange={e => upd(l._i, "unite", e.target.value)} className="w-12 border border-stone-200 rounded px-1 py-0.5"/>}</td>
                      <td className="text-right"><input type="number" value={l.quantite} onChange={e => upd(l._i, "quantite", e.target.value)} className="w-16 border border-stone-200 rounded px-1 py-0.5 text-right"/></td>
                      <td className="text-right"><input type="number" value={l.pu} onChange={e => upd(l._i, "pu", e.target.value)} className="w-20 border border-stone-200 rounded px-1 py-0.5 text-right"/></td>
                      <td className="text-right font-semibold">{EUR(l.montant)}</td>
                      <td className="text-center">
                        {showMarket
                          ? <button onClick={() => setPriceIdx(l.id)} data-testid="estim-market-price" title="Prix marché (IA)" className="text-[#C2502F] hover:opacity-70"><Search size={13}/></button>
                          : <span className={`inline-block w-2 h-2 rounded-full ${CONF[l.niveau_confiance]?.dot}`} title={`${CONF[l.niveau_confiance]?.label} · ${l.nb_references} réf.`}/>}
                      </td>
                      <td><button onClick={() => del(l._i)} className="text-stone-300 hover:text-rose-600"><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
        <span>Ajouter une ligne dans :</span>
        <select onChange={e => { if (e.target.value) { addManual(e.target.value); e.target.value = ""; } }} className="border border-stone-300 rounded px-2 py-1" data-testid="estim-add-lot-select">
          <option value="">— choisir un lot —</option>
          {allLots.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <PriceSuggestModal open={priceIdx != null} line={lignes.find(l => l.id === priceIdx) || null} onApply={(pu) => applyPrice(priceIdx, pu)} onClose={() => setPriceIdx(null)} />
    </div>
  );
}

/* ---------------- Import de devis (enrichissement bibliothèque) ---------------- */
function ImportDevisModal({ open, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState("");
  const [results, setResults] = useState(null); // [{filename,fournisseur,projet,date,lignes,counts}]
  const [committing, setCommitting] = useState(false);
  useEffect(() => { if (!open) { setFiles([]); setResults(null); setProgress(""); } }, [open]);
  if (!open) return null;

  const analyze = async () => {
    if (!files.length) { toast.error("Choisissez un ou plusieurs PDF"); return; }
    setAnalyzing(true); const out = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(`Analyse ${i + 1}/${files.length} — ${files[i].name}`);
      try {
        const fd = new FormData(); fd.append("file", files[i]);
        const { data } = await api.post("/estimatif/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        out.push(data);
      } catch (e) { toast.error(`${files[i].name} : ${e?.response?.data?.detail || "extraction impossible"}`); }
    }
    setResults(out); setAnalyzing(false); setProgress("");
    if (!out.length) toast.error("Aucun fichier exploitable");
  };
  const setLine = (ri, id, k, v) => setResults(rs => rs.map((r, i) => i === ri ? { ...r, lignes: r.lignes.map(l => l.id === id ? { ...l, [k]: v } : l) } : r));
  const commit = async () => {
    setCommitting(true); let obs = 0, cre = 0;
    try {
      for (const r of results) {
        const { data } = await api.post("/estimatif/import/commit", { source: `Devis ${r.fournisseur || r.filename || ""}`.trim(), fournisseur: r.fournisseur, projet: r.projet, date: r.date, lignes: r.lignes });
        obs += data.observations_added; cre += data.ouvrages_created;
      }
      toast.success(`Import : ${obs} prix ajoutés · ${cre} ouvrages créés (${results.length} devis)`);
      onDone && onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur import"); }
    finally { setCommitting(false); }
  };
  const STATUT = { reconnu: { c: "text-emerald-700 bg-emerald-50", l: "Reconnu" }, ambigu: { c: "text-amber-700 bg-amber-50", l: "Ambigu" }, a_creer: { c: "text-blue-700 bg-blue-50", l: "À créer" } };
  const totalLines = results ? results.reduce((n, r) => n + r.lignes.length, 0) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="import-modal">
      <div className="bg-white rounded-lg p-5 w-full max-w-4xl my-8 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Upload size={18} className="text-[#C2502F]"/> Importer des devis — enrichir la bibliothèque</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="import-close"><X size={18}/></button>
        </div>

        {!results && (
          <div className="space-y-3">
            <p className="text-sm text-stone-500">Sélectionnez <b>un ou plusieurs</b> devis / bordereaux PDF (GEOX, resto, etc.). L'IA extrait les lignes chiffrées et les rapproche de vos ouvrages. Les prix enrichissent l'historique <b>sans jamais écraser</b> vos prix conseillés.</p>
            <input type="file" accept="application/pdf" multiple onChange={e => setFiles(Array.from(e.target.files || []))} className="text-sm" data-testid="import-file"/>
            {files.length > 0 && <div className="text-xs text-stone-500">{files.length} fichier(s) sélectionné(s)</div>}
            <div className="flex items-center gap-2">
              <button onClick={analyze} disabled={analyzing || !files.length} data-testid="import-analyze-btn" className="bg-[#C2502F] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">{analyzing ? <><Loader2 size={14} className="animate-spin"/> {progress || "Analyse…"}</> : <><Wand2 size={14}/> Analyser {files.length > 1 ? `les ${files.length} devis` : "le devis"}</>}</button>
              {analyzing && <span className="text-xs text-stone-400">IA ~20-40s / fichier</span>}
            </div>
          </div>
        )}

        {results && (
          <div className="space-y-4">
            <div className="text-sm text-stone-500">{results.length} devis · {totalLines} lignes chiffrées</div>
            <div className="max-h-[52vh] overflow-y-auto border border-stone-200 rounded divide-y divide-stone-100">
              {results.map((r, ri) => (
                <div key={ri}>
                  <div className="text-sm bg-stone-50 sticky top-0 p-2 flex flex-wrap gap-x-4 gap-y-1 border-b">
                    <span><b>{r.filename}</b></span>
                    <span>Fournisseur : {r.fournisseur || "—"}</span>
                    <span>Date : {r.date || "—"}</span>
                    <span className="text-emerald-700">{r.counts?.reconnu || 0} rec.</span>
                    <span className="text-amber-700">{r.counts?.ambigu || 0} amb.</span>
                    <span className="text-blue-700">{r.counts?.a_creer || 0} à créer</span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {r.lignes.map(l => (
                        <tr key={l.id} className="border-b border-stone-50" data-testid="import-line">
                          <td className="px-2 py-1 w-16"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${STATUT[l.statut]?.c}`}>{STATUT[l.statut]?.l}</span></td>
                          <td className="text-stone-500 max-w-[110px] truncate" title={l.lot}>{l.lot}</td>
                          <td><input value={l.designation} onChange={e => setLine(ri, l.id, "designation", e.target.value)} className="w-full border border-stone-200 rounded px-1 py-0.5"/></td>
                          <td className="text-stone-500 w-10">{l.unite}</td>
                          <td className="text-right w-16"><input type="number" value={l.pu_ht} onChange={e => setLine(ri, l.id, "pu_ht", Number(e.target.value))} className="w-16 border border-stone-200 rounded px-1 py-0.5 text-right"/></td>
                          <td className="w-28"><select value={l.action} onChange={e => setLine(ri, l.id, "action", e.target.value)} className="border border-stone-200 rounded px-1 py-0.5 w-full" data-testid="import-action">
                            {l.ouvrage_id && <option value="add_obs">+ prix (obs.)</option>}
                            <option value="create">Créer ouvrage</option>
                            <option value="skip">Ignorer</option>
                          </select></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setResults(null)} className="border border-stone-300 px-4 py-2 rounded text-sm font-semibold">← Autres fichiers</button>
              <button onClick={commit} disabled={committing} data-testid="import-commit-btn" className="bg-[#C2502F] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5">{committing ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Valider l'import ({results.length} devis)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Bibliothèque d'ouvrages ---------------- */
function Bibliotheque() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [openLots, setOpenLots] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const [drag, setDrag] = useState(null);
  const load = async () => { const { data } = await api.get("/estimatif/ouvrages", { params: q ? { q } : {} }); setRows(data); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const savePu = async (o, pu) => { await api.put(`/estimatif/ouvrages/${o.id}`, { pu_conseille: Number(pu) }); load(); };
  const saveField = async (o, patch) => { await api.put(`/estimatif/ouvrages/${o.id}`, patch); load(); };
  const delOuvrage = async (o) => { await api.delete(`/estimatif/ouvrages/${o.id}`); load(); };
  const renameLot = async (lot, nv) => { if (!nv || nv === lot) return; await api.post("/estimatif/ouvrages/rename-lot", { lot, new_lot: nv }); toast.success("Lot renommé"); load(); };
  const deleteLot = async (lot, n) => { if (!window.confirm(`Supprimer le lot « ${lot} » et ses ${n} ouvrage(s) ?`)) return; await api.post("/estimatif/ouvrages/delete-lot", { lot }); toast.success("Lot supprimé"); load(); };
  const setFamille = async (lot, famille) => { await api.post("/estimatif/ouvrages/set-famille", { lot, famille }); };
  const addLot = async () => { const nom = window.prompt("Nom du nouveau lot :"); if (!nom) return; await api.post("/estimatif/ouvrages", { lot: nom.trim(), sous_lot: "", description: "Nouvel ouvrage", unite: "u", pu_conseille: 0 }); toast.success("Lot ajouté"); load(); };
  const addOuvrage = async (lot, sous_lot) => { await api.post("/estimatif/ouvrages", { lot, sous_lot: sous_lot === "—" ? "" : sous_lot, description: "Nouvel ouvrage", unite: "u", pu_conseille: 0 }); load(); };
  const promoteSub = async (lot, sous_lot) => { if (sous_lot === "—") return; await api.post("/estimatif/ouvrages/promote-sous-lot", { lot, sous_lot }); toast.success(`« ${sous_lot} » est maintenant un lot`); load(); };
  const promoteLotToFamille = async (lot) => { await api.post("/estimatif/ouvrages/set-famille", { lot, famille: lot }); toast.success(`« ${lot} » est maintenant une famille`); load(); };
  const defFam = (lot) => { const u = (lot || "").toUpperCase(); if (u.includes("CUISINE")) return "CUISINE"; if (u.includes("MOBILIER") || u.includes("AGENCEMENT")) return "MOBILIER"; return ""; };
  const famOf = (o) => o.famille || defFam(o.lot);
  const pad2 = (n) => String(n).padStart(2, "0");

  const tree = useMemo(() => {
    const t = {};
    rows.forEach(o => {
      const fam = famOf(o); const lot = o.lot || "DIVERS"; const sub = o.sous_lot || "—";
      t[fam] = t[fam] || {}; t[fam][lot] = t[fam][lot] || {}; (t[fam][lot][sub] = t[fam][lot][sub] || []).push(o);
    });
    return t;
  }, [rows]); // eslint-disable-line

  const families = Object.keys(tree).filter(f => f).sort();
  const rootLots = tree[""] ? Object.keys(tree[""]).sort() : [];
  // Numérotation DPGF : lots numérotés 01..N dans l'ordre d'affichage
  const lotOrder = [...families.flatMap(f => Object.keys(tree[f]).sort()), ...rootLots];
  const lotNum = {}; lotOrder.forEach((l, i) => { lotNum[l] = pad2(i + 1); });

  const onDropTo = async (targetFam) => { if (!drag) return; const d = drag; await setFamille(d, targetFam); setDrag(null); load(); toast.success(`« ${d} » rangé dans ${targetFam || "racine"}`); };

  const LotBlock = (lot, subs) => {
    const lotCount = Object.values(subs).reduce((n, arr) => n + arr.length, 0);
    const lotOpen = !!openLots[lot]; const code = lotNum[lot] || "";
    return (
      <div key={lot} data-testid="biblio-lot" onDragOver={e => e.preventDefault()} onDrop={e => { e.stopPropagation(); onDropTo(famOf({ lot }) || lot); }}>
        <div className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-stone-50">
          <span draggable onDragStart={() => setDrag(lot)} className="cursor-grab shrink-0 text-stone-300" title="Glisser pour ranger" data-testid="biblio-lot-drag"><GripVertical size={14}/></span>
          <button onClick={() => setOpenLots(o => ({ ...o, [lot]: !lotOpen }))} className="shrink-0" data-testid="biblio-lot-toggle"><ChevronRight size={15} className={`transition-transform ${lotOpen ? "rotate-90" : ""}`}/></button>
          <span className="text-xs font-mono text-[#C2502F] font-bold shrink-0">{code}</span>
          <input defaultValue={lot} key={lot} onBlur={e => renameLot(lot, e.target.value.trim())} onKeyDown={e => e.key === "Enter" && e.target.blur()} className="font-semibold text-sm border border-transparent hover:border-stone-200 focus:border-stone-300 rounded px-1 py-0.5 flex-1 min-w-0" data-testid="biblio-lot-name"/>
          <span className="text-xs text-stone-400 shrink-0">{lotCount}</span>
          <button onClick={() => promoteLotToFamille(lot)} title="Transformer ce lot en famille" data-testid="biblio-promote-lot" className="w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0 hover:scale-110 transition-transform shadow-sm" style={{ background: "linear-gradient(135deg,#f472b6,#fb923c,#a78bfa)" }}><ArrowLeft size={11}/></button>
          <button onClick={() => deleteLot(lot, lotCount)} className="text-stone-300 hover:text-rose-600 shrink-0" data-testid="biblio-lot-del" title="Supprimer le lot"><X size={15}/></button>
        </div>
        {lotOpen && (
          <div className="pl-6 pb-2 bg-stone-50/40">
            {Object.keys(subs).sort().map((sub, si) => {
              const key = lot + "||" + sub; const subOpen = !!openSubs[key]; const items = subs[sub];
              return (
                <div key={key}>
                  <div className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-white rounded">
                    <button onClick={() => setOpenSubs(o => ({ ...o, [key]: !subOpen }))} className="flex items-center gap-1.5 flex-1 text-left min-w-0" data-testid="biblio-sub-toggle">
                      <ChevronRight size={13} className={`transition-transform shrink-0 ${subOpen ? "rotate-90" : ""}`}/>
                      <span className="font-mono text-xs text-stone-400 shrink-0">{code}.{si + 1}</span>
                      <span className="text-sm text-stone-600 truncate">{sub}</span>
                    </button>
                    {sub !== "—" && <button onClick={() => promoteSub(lot, sub)} title="Transformer ce sous-lot en lot" data-testid="biblio-promote-sub" className="w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0 hover:scale-110 transition-transform shadow-sm" style={{ background: "linear-gradient(135deg,#f472b6,#fb923c,#a78bfa)" }}><ArrowLeft size={11}/></button>}
                    <span className="text-[11px] text-stone-400 shrink-0">{items.length}</span>
                  </div>
                  {subOpen && (
                    <div className="overflow-x-auto pl-2">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-stone-400 border-b"><th className="py-1 px-2 w-12">Réf.</th><th>Description</th><th>Unité</th><th className="text-right">PU conseillé</th><th className="text-right">Médiane</th><th className="text-center">Réf.</th><th></th></tr></thead>
                        <tbody>
                          {items.map((o, oi) => (
                            <tr key={o.id} className="border-b border-stone-100" data-testid="biblio-row">
                              <td className="px-2 font-mono text-[11px] text-stone-400">{code}.{si + 1}.{oi + 1}</td>
                              <td className="pr-2"><input defaultValue={o.description} key={o.description} onBlur={e => e.target.value !== o.description && saveField(o, { description: e.target.value })} className="w-full border border-transparent hover:border-stone-200 rounded px-1 py-0.5" data-testid="biblio-ouvrage-desc"/></td>
                              <td><input defaultValue={o.unite} key={o.unite} onBlur={e => e.target.value !== o.unite && saveField(o, { unite: e.target.value })} className="w-12 border border-transparent hover:border-stone-200 rounded px-1 py-0.5 text-stone-500"/></td>
                              <td className="text-right"><input type="number" defaultValue={o.pu_conseille} onBlur={e => e.target.value != o.pu_conseille && savePu(o, e.target.value)} className="w-20 border border-stone-200 rounded px-1 py-0.5 text-right"/></td>
                              <td className="text-right text-stone-500">{o.pu_median ? EUR(o.pu_median) : "—"}</td>
                              <td className="text-center text-stone-500">{o.nb_references}</td>
                              <td><button onClick={() => delOuvrage(o)} className="text-stone-300 hover:text-rose-600" title="Supprimer"><Trash2 size={13}/></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button onClick={() => addOuvrage(lot, sub)} className="text-xs text-[#C2502F] font-semibold px-2 py-1" data-testid="biblio-add-ouvrage">+ ouvrage</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Rechercher un ouvrage…" className="border border-stone-300 rounded px-3 py-2 text-sm flex-1 max-w-md" data-testid="biblio-search"/>
        <button onClick={load} className="border border-stone-300 px-3 py-2 rounded text-sm font-semibold">Rechercher</button>
        <button onClick={addLot} data-testid="biblio-add-lot" className="border border-stone-300 px-3 py-2 rounded text-sm font-semibold flex items-center gap-1.5"><Plus size={15}/> Ajouter un lot</button>
        <button onClick={() => setShowImport(true)} data-testid="biblio-import-btn" className="bg-[#C2502F] text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-1.5 ml-auto"><Upload size={15}/> Importer un devis</button>
      </div>
      <p className="text-xs text-stone-500">{rows.length} ouvrages · <b>Famille → Lot → Sous-lot</b> (références DPGF auto) · glissez un lot sur une famille pour le ranger · cliquez un intitulé pour le renommer.</p>
      <div className="bg-white border border-stone-200 rounded-lg divide-y divide-stone-100">
        {families.map(fam => (
          <div key={fam} data-testid="biblio-famille" onDragOver={e => e.preventDefault()} onDrop={() => onDropTo(fam)}>
            <div className="px-3 py-2 bg-stone-100/70 font-display font-bold text-[#C2502F] text-sm flex items-center justify-between" data-testid="biblio-famille-head">
              <span>{fam}</span><span className="text-[11px] text-stone-400 font-normal">déposez un lot ici</span>
            </div>
            <div className="divide-y divide-stone-100">{Object.keys(tree[fam]).sort().map(lot => LotBlock(lot, tree[fam][lot]))}</div>
          </div>
        ))}
        {rootLots.length > 0 && (
          <div data-testid="biblio-root" onDragOver={e => e.preventDefault()} onDrop={() => onDropTo("")}>
            <div className="divide-y divide-stone-100">{rootLots.map(lot => LotBlock(lot, tree[""][lot]))}</div>
          </div>
        )}
        {rows.length === 0 && <div className="p-6 text-center text-sm text-stone-400">Aucun ouvrage.</div>}
      </div>
      <ImportDevisModal open={showImport} onClose={() => setShowImport(false)} onDone={load} />
    </div>
  );
}

/* ---------------- Questionnaire (éditeur admin) ---------------- */
function QuestionnaireAdmin() {
  const [schema, setSchema] = useState(null);
  const [lots, setLots] = useState([]);
  const [open, setOpen] = useState({}); // section id -> bool
  const [saving, setSaving] = useState(false);
  const uid = () => Math.random().toString(36).slice(2, 10);

  useEffect(() => {
    api.get("/estimatif/questionnaire").then(({ data }) => setSchema(data));
    api.get("/estimatif/lots").then(({ data }) => setLots(data.lots.map(l => l.lot)));
  }, []);

  const commit = (mut) => setSchema(prev => {
    const s = structuredClone(prev); mut(s); return s;
  });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/estimatif/questionnaire", { sections: schema.sections }); setSchema(data); toast.success("Questionnaire enregistré (v" + data.version + ")"); }
    finally { setSaving(false); }
  };

  if (!schema) return <div className="p-8 text-center text-stone-400"><Loader2 className="animate-spin inline"/></div>;

  const addSection = () => commit(s => s.sections.push({ id: uid(), code: "?", titre: "Nouvelle section", module: "base", description: "", condition: null, order: s.sections.length + 1, questions: [] }));

  return (
    <div className="space-y-3" data-testid="questionnaire-admin">
      <div className="flex items-center justify-between sticky top-0 bg-stone-50 py-2 z-10">
        <p className="text-xs text-stone-500">Éditeur du questionnaire (v{schema.version}) — sections, questions, réponses, conditions et règles génératrices. Tout est administrable.</p>
        <div className="flex gap-2">
          <button onClick={addSection} className="border border-stone-300 px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1" data-testid="qadmin-add-section"><Plus size={14}/> Section</button>
          <button onClick={save} disabled={saving} className="bg-[#C2502F] text-white px-4 py-1.5 rounded text-sm font-semibold flex items-center gap-1.5" data-testid="qadmin-save"><Save size={14}/> Enregistrer</button>
        </div>
      </div>

      {schema.sections.map((sec, si) => (
        <div key={sec.id} className="bg-white border border-stone-200 rounded-lg" data-testid={`qadmin-section-${si}`}>
          <div className="flex items-center gap-2 p-3">
            <button onClick={() => setOpen(o => ({ ...o, [sec.id]: !o[sec.id] }))} className="text-stone-400 w-5">{open[sec.id] ? "▾" : "▸"}</button>
            <input value={sec.code} onChange={e => commit(s => { s.sections[si].code = e.target.value; })} className="w-12 border border-stone-200 rounded px-1 py-0.5 text-sm font-bold text-[#C2502F]"/>
            <input value={sec.titre} onChange={e => commit(s => { s.sections[si].titre = e.target.value; })} className="flex-1 border border-stone-200 rounded px-2 py-0.5 text-sm font-semibold"/>
            <select value={sec.module} onChange={e => commit(s => { s.sections[si].module = e.target.value; })} className="border border-stone-200 rounded px-1 py-0.5 text-xs">
              {["base", "restauration", "centre"].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button onClick={() => commit(s => s.sections.splice(si, 1))} className="text-stone-300 hover:text-rose-600"><Trash2 size={15}/></button>
          </div>
          {open[sec.id] && (
            <div className="px-3 pb-3 space-y-2 border-t border-stone-100 pt-2">
              <CondEditor label="Section visible si" cond={sec.condition} onChange={c => commit(s => { s.sections[si].condition = c; })} />
              {(sec.questions || []).map((q, qi) => (
                <div key={q.id} className="border border-stone-200 rounded p-2 bg-stone-50/50" data-testid={`qadmin-q-${si}-${qi}`}>
                  <div className="flex items-center gap-2">
                    <input value={q.label} onChange={e => commit(s => { s.sections[si].questions[qi].label = e.target.value; })} placeholder="Libellé" className="flex-1 border border-stone-200 rounded px-2 py-0.5 text-sm"/>
                    <input value={q.key} onChange={e => commit(s => { s.sections[si].questions[qi].key = e.target.value; })} placeholder="clé" className="w-28 border border-stone-200 rounded px-2 py-0.5 text-xs font-mono"/>
                    <select value={q.type} onChange={e => commit(s => { s.sections[si].questions[qi].type = e.target.value; })} className="border border-stone-200 rounded px-1 py-0.5 text-xs">
                      {["single", "multi", "number", "boolean", "etat", "text"].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input value={q.unit || ""} onChange={e => commit(s => { s.sections[si].questions[qi].unit = e.target.value; })} placeholder="unité" className="w-16 border border-stone-200 rounded px-1 py-0.5 text-xs"/>
                    <button onClick={() => commit(s => s.sections[si].questions.splice(qi, 1))} className="text-stone-300 hover:text-rose-600"><Trash2 size={14}/></button>
                  </div>
                  <CondEditor label="Question visible si" cond={q.condition} onChange={c => commit(s => { s.sections[si].questions[qi].condition = c; })} small />
                  {["single", "multi", "etat", "boolean"].includes(q.type) && (
                    <div className="mt-2 ml-2 space-y-1.5">
                      {(q.options || []).map((o, oi) => (
                        <div key={o.id} className="border-l-2 border-stone-200 pl-2">
                          <div className="flex items-center gap-2">
                            <span className="text-stone-300 text-xs">◦</span>
                            <input value={o.label} onChange={e => commit(s => { s.sections[si].questions[qi].options[oi].label = e.target.value; })} className="w-44 border border-stone-200 rounded px-1.5 py-0.5 text-xs"/>
                            <span className="text-[10px] text-stone-400">→ {(o.generates || []).length} règle(s)</span>
                            <button onClick={() => commit(s => { const opt = s.sections[si].questions[qi].options[oi]; (opt.generates = opt.generates || []).push({ lot: lots[0] || "", match: "", formule: "1" }); })} className="text-[10px] text-[#C2502F] font-semibold" data-testid="qadmin-add-rule">+ règle</button>
                            <button onClick={() => commit(s => s.sections[si].questions[qi].options.splice(oi, 1))} className="text-stone-300 hover:text-rose-600 ml-auto"><Trash2 size={12}/></button>
                          </div>
                          {(o.generates || []).map((rule, ri) => (
                            <div key={ri} className="flex items-center gap-1 mt-1 ml-4 text-xs">
                              <select value={rule.lot} onChange={e => commit(s => { s.sections[si].questions[qi].options[oi].generates[ri].lot = e.target.value; })} className="border border-stone-200 rounded px-1 py-0.5 text-[11px] max-w-[160px]">
                                {lots.map(l => <option key={l} value={l}>{l}</option>)}
                              </select>
                              <input value={rule.match || ""} onChange={e => commit(s => { s.sections[si].questions[qi].options[oi].generates[ri].match = e.target.value; })} placeholder="mot-clé ouvrage" className="w-32 border border-stone-200 rounded px-1 py-0.5 text-[11px]"/>
                              <input value={rule.formule || ""} onChange={e => commit(s => { s.sections[si].questions[qi].options[oi].generates[ri].formule = e.target.value; })} placeholder="formule (ex: surface_sol)" className="w-40 border border-stone-200 rounded px-1 py-0.5 text-[11px] font-mono"/>
                              <button onClick={() => commit(s => s.sections[si].questions[qi].options[oi].generates.splice(ri, 1))} className="text-stone-300 hover:text-rose-600"><Trash2 size={11}/></button>
                            </div>
                          ))}
                        </div>
                      ))}
                      <button onClick={() => commit(s => { const opts = s.sections[si].questions[qi].options; opts.push({ id: uid(), label: "Nouvelle réponse", value: "Nouvelle réponse", generates: [] }); })} className="text-xs text-stone-500 hover:text-stone-800 ml-2">+ réponse</button>
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => commit(s => s.sections[si].questions.push({ id: uid(), label: "Nouvelle question", key: "q_" + uid(), type: "single", options: [], unit: "", condition: null }))} className="text-xs font-semibold text-[#C2502F]" data-testid={`qadmin-add-q-${si}`}>+ Question</button>
            </div>
          )}
        </div>
      ))}
      <div className="text-[11px] text-stone-400 bg-stone-100 rounded p-3">
        <b>Formules disponibles :</b> surface_totale, surface_public, surface_reserve, surface_cuisine, surface_sol, surface_murale, plafond_surface, hauteur, facade_ml, facade_ml_eff, cloisons_ml_eff, perimetre, budget_cible. Opérateurs + - * / ( ) et sqrt/max/min. Exemple : <code>cloisons_ml_eff * hauteur</code>.
      </div>
    </div>
  );
}

function CondEditor({ label, cond, onChange, small }) {
  const key = cond?.question_key || "";
  const vals = (cond?.in || []).join(", ");
  const set = (k, v) => {
    const nk = k === "key" ? v : key;
    const nv = k === "vals" ? v.split(",").map(x => x.trim()).filter(Boolean) : (cond?.in || []);
    if (!nk && (!nv || nv.length === 0)) return onChange(null);
    onChange({ question_key: nk, in: nv });
  };
  return (
    <div className={`flex items-center gap-1 ${small ? "text-[10px] mt-1" : "text-xs"} text-stone-400`}>
      <span>{label}</span>
      <input value={key} onChange={e => set("key", e.target.value)} placeholder="clé question" className="w-28 border border-stone-200 rounded px-1 py-0.5 font-mono"/>
      <span>∈</span>
      <input value={vals} onChange={e => set("vals", e.target.value)} placeholder="valeurs (séparées par ,)" className="w-52 border border-stone-200 rounded px-1 py-0.5"/>
      {cond && <button onClick={() => onChange(null)} className="text-stone-300 hover:text-rose-600">✕</button>}
    </div>
  );
}
